import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ImagePlus, X } from 'lucide-react';
import { storage, devicePhotos as photosApi } from '@/lib/trackizy';
import PhotoLightbox from './PhotoLightbox';
import { showToast } from './Toast';
import { useConfirmDialog } from './ConfirmDialog';

const MAX_PHOTOS = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export default function DevicePhotos({ deviceId, userId }) {
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const orgId = useSelector((state) => state.org?.id);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);

  const fetchPhotos = useCallback(async () => {
    if (!deviceId) return;
    try {
      const data = await photosApi.list(deviceId);
      if (Array.isArray(data)) setPhotos(data);
    } catch (err) {
      console.error('Failed to fetch device photos:', err);
    }
  }, [deviceId]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const getPublicUrl = (filePath) => {
    return storage.getPublicUrl(filePath);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    e.target.value = '';

    if (file.size > MAX_FILE_SIZE) {
      showToast(t('settings.photoFileTooLarge'));
      return;
    }
    if (photos.length >= MAX_PHOTOS) {
      showToast(t('settings.photoLimitReached'));
      return;
    }

    setLoading(true);
    try {
      // Upload file via trackizy storage API
      const uploadResult = await storage.upload('device-photos', file);
      const publicUrl = uploadResult?.url || getPublicUrl(uploadResult?.path);

      // Create photo record
      await photosApi.create(deviceId, {
        organization_id: orgId,
        file_path: uploadResult?.path || `${deviceId}/${file.name}`,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type,
        sort_order: photos.length,
        uploaded_by: userId,
      });

      await fetchPhotos();
    } catch (err) {
      console.error('Photo upload error:', err);
      showToast(t('settings.photoUploadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (photo) => {
    if (!await confirm({ title: t('settings.confirmDeletePhoto'), variant: 'destructive' })) return;

    setLoading(true);
    try {
      await photosApi.delete(deviceId, photo.id);
      await fetchPhotos();
    } catch (err) {
      console.error('Photo delete error:', err);
      showToast(t('settings.photoDeleteError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 flex-wrap">
        {photos.map((photo) => (
          <div
            key={photo.id}
            className="relative w-20 h-20 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 overflow-hidden group cursor-pointer flex-shrink-0"
            onClick={() => setLightbox(photo)}
          >
            <img
              src={getPublicUrl(photo.file_path)}
              alt={photo.file_name}
              className="w-full h-full object-cover"
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(photo);
              }}
              className="absolute top-0.5 end-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X size={12} />
            </button>
          </div>
        ))}

        {photos.length < MAX_PHOTOS && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={loading}
            className="w-20 h-20 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl text-slate-400 hover:text-slate-500 hover:border-slate-400 dark:hover:border-slate-500 transition-colors disabled:opacity-50 flex-shrink-0"
          >
            <ImagePlus size={18} />
            <span className="text-[10px]">{t('settings.addPhoto')}</span>
          </button>
        )}

        <span className="text-[10px] text-slate-400">{photos.length}/{MAX_PHOTOS}</span>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleUpload}
      />

      {lightbox && (
        <PhotoLightbox
          src={getPublicUrl(lightbox.file_path)}
          alt={lightbox.file_name}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
